import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { FaLongArrowAltLeft, FaLongArrowAltRight } from 'react-icons/fa';

/**
 * Primary UI component for user interaction
 */
const Button = ({
  variant,
  size,
  children,
  onClick,
  doShowLoadingState,
  direction,
  className,
  disabled,
  doDiscardRadius,
}) => {
  const style = (() => {
    const s = {
      bgColor: 'bg-button-theme',
      text: 'text-button-theme text-xs font-bold',
      border: 'border',
      size: 'py-3 w-full',
      cursor: '',
    };

    switch (variant) {
      case Button.buttonVariants.SECONDARY: {
        s.bgColor = 'bg-grey-500';
        break;
      }
      case Button.buttonVariants.OUTLINE: {
        s.bgColor = '';
        s.text = 'text-xs font-bold text-theme';
        s.border = 'border border-theme';
        s.size = 'w-auto';
        break;
      }
      case Button.buttonVariants.OUTLINE_WHITE_BG: {
        s.bgColor = 'bg-fields-0';
        s.text = 'text-xs';
        s.border = 'border-2';
        s.size = 'w-auto';
        break;
      }
      case Button.buttonVariants.LINK: {
        s.bgColor = '';
        s.text = 'text-theme';
        s.cursor = 'cursor-pointer';
        s.border = '';
        s.size = '';
        break;
      }
      case Button.buttonVariants.INLINE: {
        s.bgColor = 'bg-white';
        s.text = 'text-theme text-xs font-bold';
        s.border = 'border-2 border-theme border-opacity-300';
        break;
      }
      case Button.buttonVariants.ARROW_LINK: {
        s.bgColor = '';
        s.text = 'text-gray-600 block';
        s.border = '';
        s.size = 'w-auto mt-6 mb-6';
        break;
      }
      default: {
        break;
      }
    }

    switch (size) {
      case Button.buttonSizes.XS: {
        if (variant === Button.buttonVariants.LINK) {
          s.size = 'mx-2 py-1';
        } else {
          s.size = `px-2 ${variant === Button.buttonVariants.INLINE ? 'py-2' : 'py-1'}`;
        }
        break;
      }
      case Button.buttonSizes.SM: {
        s.size = 'py-2 px-4 whitespace-nowrap md:whitespace-nowrap';
        break;
      }
      default: {
        break;
      }
    }

    return s;
  })();

  // When the caller passes its own text-size class, drop the variant's:
  // which one renders is otherwise decided by CSS order, and Tailwind v3
  // orders font-size utilities differently than v1 did (the caller's larger
  // size used to win by coincidence of v1's scale order).
  const fontSizeClass = /(^|\s)!?text-(xs|sm|base|lg|xl|\dxl|10)(\s|$)/;
  const variantText = fontSizeClass.test(className || '')
    ? style.text.replace(fontSizeClass, ' ').trim()
    : style.text;

  return (
    <button
      type="button"
      className={clsx(
        style.bgColor,
        style.border,
        style.size,
        variantText,
        className,
        'text-center',
        doDiscardRadius ? '' : 'rounded-2xl',
        doShowLoadingState || disabled ? 'cursor-not-allowed opacity-50' : '',
      )}
      onClick={onClick}
      disabled={doShowLoadingState || disabled}
    >
      {variant === Button.buttonVariants.ARROW_LINK && direction > -1 && (
        <>
          {direction === Button.directions.LEFT ? (
            <FaLongArrowAltLeft className="inline-block" />
          ) : (
            <FaLongArrowAltRight className="inline-block" />
          )}{' '}
        </>
      )}
      {children}
      {doShowLoadingState ? (
        <svg
          className="animate-spin ml-2 h-5 w-5 inline-block"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      ) : (
        <></>
      )}
    </button>
  );
};

Button.propTypes = {
  variant: PropTypes.number,
  size: PropTypes.number,
  /**
   * Optional click handler
   */
  onClick: PropTypes.func,
  /**
   * Optional: do show a spinner and disable button
   */
  doShowLoadingState: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  direction: PropTypes.number,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  doDiscardRadius: PropTypes.bool,
};

Button.buttonVariants = {
  PRIMARY: 0,
  SECONDARY: 1,
  OUTLINE: 2,
  OUTLINE_WHITE_BG: 3,
  LINK: 4,
  INLINE: 5,
  ARROW_LINK: 6,
};

Button.directions = {
  LEFT: 0,
  RIGHT: 1,
};

Button.buttonSizes = {
  XS: 0,
  SM: 1,
  MD: 2,
};

Button.defaultProps = {
  variant: Button.buttonVariants.PRIMARY,
  size: Button.buttonSizes.MD,
  onClick: undefined,
  doShowLoadingState: false,
  children: undefined,
  direction: undefined,
  className: '',
  disabled: false,
  doDiscardRadius: false,
};

export default Button;
